import { InnerBox } from "@/components/elements/InnerBox";
import { FC, useMemo, useState } from "react";
import { Stack, Step, StepConnector, stepConnectorClasses, StepIconProps, StepLabel, Stepper, styled, Typography, Box, Link } from "@mui/material";
import { IconCheck } from "@/assets/icons";
import { background, black, blue, state, text } from "@/theme/colors";
import { Button } from "@/components/elements/Button";
import { useGetMatchInfo, updateTutorialHiddenApi } from "@/api/services/main/match";
import { useGetUserSettingInfo } from "@/api/services/main/user-setting";
import { useCompanyList } from "@/api/services/main/company";
import { useJobList } from "@/api/services/main/job";
import { useTalentList } from "@/api/services/main/talent";
import { useMe } from "@/api/services/main/auth";
import { useVisible } from "@/hooks/useVisible";
import { ConfirmHideTutorialModal } from "@/features/dashboard/components/ConfirmHideTutorialModal";
import { useQueryClient } from "@tanstack/react-query";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { STEPS_TUTORIAL } from "@/features/dashboard/constants/stepTutorial";
import { LoadingArea } from "@/components/elements/Loading/Loading";

const getStepContentColor = (stepIndex: number, activeStep: number): string => {
  if (stepIndex < activeStep) return black[90];
  if (stepIndex === activeStep + 1) return black[30];
  return black[70];
};

type TDashboardInitialSetupProps = {
  onHide?: () => void;
};

export const DashboardInitialSetup: FC<TDashboardInitialSetupProps> = ({ onHide }) => {
  const { data: matchInfoResult, isLoading: isLoadingMatchInfo } = useGetMatchInfo();
  const { data: userSettingResult, isLoading: isLoadingUserSetting } = useGetUserSettingInfo();
  const { data: companyListResult, isLoading: isLoadingCompanyList } = useCompanyList({ page: 1, perPage: 1 });
  const { data: jobListResult, isLoading: isLoadingJobList } = useJobList({ page: 1, perPage: 1 });
  const { data: talentListResult, isLoading: isLoadingTalentList } = useTalentList({ page: 1, perPage: 1 });
  const { data: meResult, isLoading: isLoadingMe } = useMe();

  const confirmModal = useVisible();
  const [isHiding, setIsHiding] = useState(false);
  const queryClient = useQueryClient();
  const isIrohanaSupport = meResult?.data?.isMatchUser === false;

  const isLoading = isLoadingMatchInfo || isLoadingUserSetting || isLoadingCompanyList || isLoadingJobList || isLoadingTalentList || isLoadingMe;

  const isStep1Complete = useMemo(() => {
    if (!matchInfoResult?.data) return false;
    const match = matchInfoResult.data;
    const hasMatchName = !!match.matchName?.trim();
    const hasLicenseNumber = !!match.matchLicenseNumber?.trim();
    const hasOfficeName = !!match.matchOffices?.[0]?.officeName?.trim();
    return hasMatchName && hasLicenseNumber && hasOfficeName;
  }, [matchInfoResult]);

  const isStep2Complete = useMemo(() => {
    if (!userSettingResult?.data) return false;
    return !!userSettingResult.data.userFullName?.trim();
  }, [userSettingResult]);

  const isStep3Complete = useMemo(() => {
    const hasCompany = (companyListResult?.pagination?.total ?? 0) > 0;
    const hasJob = (jobListResult?.pagination?.total ?? 0) > 0;
    const hasTalent = (talentListResult?.pagination?.total ?? 0) > 0;
    return hasCompany && hasJob && hasTalent;
  }, [companyListResult, jobListResult, talentListResult]);

  const activeStep = useMemo(() => {
    if (!isStep1Complete) return 0;
    if (!isStep2Complete) return 1;
    if (!isStep3Complete) return 2;
    return 4;
  }, [isStep1Complete, isStep2Complete, isStep3Complete]);

  const ColorLibStepIcon = (props: StepIconProps) => {
    const { active, completed, className, icon } = props;

    return (
      <ColorLibStepIconRoot ownerState={{ completed, active }} className={className}>
        {completed ? <IconCheck width={20} height={20} /> : <Typography sx={{ fontSize: 16, fontWeight: 500 }}>{icon}</Typography>}
      </ColorLibStepIconRoot>
    );
  };

  const handleCloseTutorial = () => {
    if (isIrohanaSupport) {
      onHide?.();
    } else {
      confirmModal.open();
    }
  };

  const handleConfirmHideTutorial = async () => {
    setIsHiding(true);
    try {
      await updateTutorialHiddenApi(true);
      await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.MATCH.FETCH_DETAIL] });
      confirmModal.close();
      onHide?.();
    } catch (error) {
      console.error("Failed to hide tutorial:", error);
    } finally {
      setIsHiding(false);
    }
  };

  if (isLoading) {
    return (
      <InnerBox title="irohana matchをご利用いただくための初期設定手順" variantTitle="head20Bold" bordered={true} showDivider={false}>
        <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", minHeight: 200 }}>
          <LoadingArea />
        </Box>
      </InnerBox>
    );
  }

  return (
    <InnerBox title="irohana matchをご利用いただくための初期設定手順" variantTitle="head20Bold" bordered={true} showDivider={false}>
      <Stack sx={{ width: "100%" }}>
        <Stepper
          activeStep={activeStep}
          orientation="vertical"
          sx={{
            "& > .MuiStepConnector-root": {
              display: "none",
            },
            mt: 1,
          }}
        >
          {STEPS_TUTORIAL.map((step, index) => {
            const isCompleted = index < activeStep;
            const isActive = index === activeStep;
            const isLastStep = index === STEPS_TUTORIAL.length - 1;
            const isThirdStep = index === 2;

            return (
              <CustomStep
                key={step.title}
                completed={isCompleted}
                active={isActive}
                className={`${isLastStep ? "last-step" : ""} ${isThirdStep ? "third-step" : ""}`}
              >
                <StepLabel StepIconComponent={ColorLibStepIcon} sx={{ alignItems: "flex-start", padding: 0, paddingBottom: isLastStep ? 0 : 3 }}>
                  <Box sx={{ mt: -0.5, ml: 2 }}>
                    <Typography sx={{ fontSize: 20, fontWeight: 500, color: getStepContentColor(index, activeStep), mb: 1 }}>{step.title}</Typography>
                    <Box sx={{ mb: 1.5 }}>
                      {step.content.map((line, lineIndex) => (
                        <Typography
                          key={lineIndex}
                          sx={{ fontSize: 14, fontWeight: 400, color: getStepContentColor(index, activeStep), lineHeight: 1.7 }}
                        >
                          {line}
                        </Typography>
                      ))}
                    </Box>
                    {step.links.length > 0 && (
                      <Stack direction="row" spacing={3} flexWrap="wrap">
                        {step.links.map((link, linkIndex) => (
                          <Link
                            key={linkIndex}
                            href={link.url}
                            sx={{
                              fontSize: 14,
                              fontWeight: 400,
                              color: state.link_1,
                              cursor: "pointer",
                              textDecoration: "underline",
                              "&:hover": {
                                color: state.link_1,
                              },
                            }}
                          >
                            {link.label}
                          </Link>
                        ))}
                      </Stack>
                    )}
                  </Box>
                </StepLabel>
                {!isLastStep && <ColorLibConnector isThirdStep={isThirdStep} isCompleted={isCompleted} />}
              </CustomStep>
            );
          })}
        </Stepper>

        <Box sx={{ display: "flex", justifyContent: "flex-end", mt: 3 }}>
          <Button variant="outline" size="sm" onClick={handleCloseTutorial}>
            このチュートリアルを閉じる
          </Button>
        </Box>
      </Stack>

      <ConfirmHideTutorialModal open={confirmModal.visible} isLoading={isHiding} onClose={confirmModal.close} onConfirm={handleConfirmHideTutorial} />
    </InnerBox>
  );
};

const CustomStep = styled(Step)(() => ({
  padding: 0,
  marginBottom: 0,
  position: "relative",
  "& .MuiStepContent-root": {
    paddingLeft: 0,
    paddingRight: 0,
    marginLeft: 24,
    borderLeft: "none",
  },
  "& .MuiStepLabel-root": {
    padding: 0,
    paddingBottom: 0,
  },
  "& .MuiStepLabel-iconContainer": {
    paddingRight: 0,
  },
}));

const ColorLibStepIconRoot = styled("div")<{
  ownerState: { completed?: boolean; active?: boolean };
}>(({ ownerState }) => ({
  backgroundColor: background.disable,
  zIndex: 1,
  color: text.tertiary,
  width: 40,
  height: 40,
  display: "flex",
  borderRadius: "50%",
  justifyContent: "center",
  alignItems: "center",
  flexShrink: 0,
  ...(ownerState.active && {
    backgroundColor: background.white,
    border: `4px solid ${blue[80]}`,
    color: blue[80],
  }),
  ...(ownerState.completed && {
    backgroundColor: blue[80],
    color: text.white,
  }),
}));

const ColorLibConnector = styled(StepConnector)<{ isThirdStep?: boolean; isCompleted?: boolean }>(({ isThirdStep, isCompleted }) => ({
  marginLeft: 18,
  marginTop: isThirdStep ? -75 : -60,
  marginBottom: isThirdStep ? -18 : -12,
  [`& .${stepConnectorClasses.line}`]: {
    minHeight: 140,
    borderLeft: `4px solid ${isCompleted ? blue[80] : background.disable}`,
  },
}));
